import {
  Circle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  confirmPasswordReset,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { auth } from "../../auth/config";
import { Routes } from "../../helpers/router";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/chackra-ui-button";
import * as Sentry from "@sentry/react";
import {
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

type FormValues = {
  password: string;
  passwordConfirmation: string;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    password: Yup.string()
      .min(6, t("usermgmt.verifyResetPasswordForm.passwordMinLength"))
      .required(t("usermgmt.verifyResetPasswordForm.fieldRequired")),
    passwordConfirmation: Yup.string()
      .min(6, t("usermgmt.verifyResetPasswordForm.passwordMinLength"))
      .required(t("usermgmt.verifyResetPasswordForm.fieldRequired"))
      .oneOf(
        [Yup.ref("password")],
        t("usermgmt.verifyResetPasswordForm.passwordsMismatch")
      ),
  });

type Props = {
  oobCode: string;
};

/** Centered status screen for success/error states */
const StatusScreen = (props: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  buttonLabel: string;
  buttonTo: string;
}) => (
  <VStack w="full" flex={1} justify="center" align="center" spacing={5}>
    <Circle size="56px" bg={props.iconBg}>
      <Icon boxSize={6} color={props.iconColor}>
        <props.icon />
      </Icon>
    </Circle>
    <Text
      fontFamily="'Barlow Condensed', sans-serif"
      fontSize="24px"
      fontWeight={700}
      color="#13192E"
      textAlign="center"
    >
      {props.title}
    </Text>
    <Text fontSize="16px" color="slate.500" textAlign="center" maxW="400px">
      {props.description}
    </Text>
    <Button as={Link} to={props.buttonTo} variant="senseiFilled" mt={2}>
      {props.buttonLabel}
    </Button>
  </VStack>
);

export const VerifyResetPasswordForm = ({ oobCode }: Props) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [alertMessage, setAlertMessage] = useState<
    "completed" | "expired" | "invalid" | undefined
  >(undefined);

  const {
    register,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema(t)),
  });

  const onSubmit = handleSubmit(
    async (values) => {
      try {
        await verifyPasswordResetCode(auth, oobCode);
        await confirmPasswordReset(auth, oobCode, values.password);
        await signOut(auth);
        setAlertMessage("completed");
      } catch (error: any) {
        if (error.code === "auth/expired-action-code") {
          setAlertMessage("expired");
        } else if (error.code === "auth/weak-password") {
          setError("password", {
            message: t("usermgmt.verifyResetPasswordForm.passwordMinLength"),
          });
        } else if (error.code === "auth/invalid-action-code") {
          setAlertMessage("invalid");
        }
        Sentry.captureException(error);
      }
    },
    (errors, event) => {
      Sentry.captureException(
        new Error("Unable to submit verify reset password form"),
        { extra: { event, errors } }
      );
    }
  );

  if (alertMessage === "expired") {
    return (
      <StatusScreen
        icon={ExclamationTriangleIcon}
        iconColor="red.500"
        iconBg="red.50"
        title={t("usermgmt.verifyResetPasswordForm.expiredLinkTitle")}
        description={t("usermgmt.verifyResetPasswordForm.expiredLinkDescription")}
        buttonLabel={t("usermgmt.verifyResetPasswordForm.requestNewLink")}
        buttonTo={Routes.ResetPassword()}
      />
    );
  }

  if (alertMessage === "invalid") {
    return (
      <StatusScreen
        icon={ExclamationTriangleIcon}
        iconColor="red.500"
        iconBg="red.50"
        title={t("usermgmt.verifyResetPasswordForm.invalidLinkTitle")}
        description={t("usermgmt.verifyResetPasswordForm.invalidLinkDescription")}
        buttonLabel={t("usermgmt.verifyResetPasswordForm.requestNewLink")}
        buttonTo={Routes.ResetPassword()}
      />
    );
  }

  if (alertMessage === "completed") {
    return (
      <StatusScreen
        icon={CheckIcon}
        iconColor="green.500"
        iconBg="green.50"
        title={t("usermgmt.verifyResetPasswordForm.passwordUpdatedTitle")}
        description={t("usermgmt.verifyResetPasswordForm.passwordUpdatedDescription")}
        buttonLabel={t("usermgmt.verifyResetPasswordForm.login")}
        buttonTo={Routes.Login()}
      />
    );
  }

  return (
    <VStack w="full" flex={1} justify="center" spacing={0}>
      <VStack w="full" maxW="440px" spacing={0} px={4}>
        <Text
          fontFamily="'Barlow Condensed', sans-serif"
          fontSize="28px"
          fontWeight={700}
          color="#13192E"
          textAlign="center"
          mb={7}
        >
          {t("usermgmt.verifyResetPasswordForm.changePassword")}
        </Text>

        <form onSubmit={onSubmit} autoComplete="off" style={{ width: "100%" }}>
          <VStack spacing={4} w="full">
            <FormControl isInvalid={!!errors.password}>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("usermgmt.verifyResetPasswordForm.newPassword")}
              </FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  pr="48px"
                  {...register("password")}
                />
                <InputRightElement h="full" pr={1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    minW="36px"
                    h="36px"
                    borderRadius="full"
                  >
                    <Icon boxSize={5} color="slate.400">
                      {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </Icon>
                  </Button>
                </InputRightElement>
              </InputGroup>
              {errors.password?.message && (
                <FormErrorMessage>{errors.password.message}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isInvalid={!!errors.passwordConfirmation}>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("usermgmt.verifyResetPasswordForm.confirmPassword")}
              </FormLabel>
              <InputGroup>
                <Input
                  id="passwordConfirmation"
                  type={showPasswordConfirmation ? "text" : "password"}
                  autoComplete="new-password"
                  pr="48px"
                  {...register("passwordConfirmation")}
                />
                <InputRightElement h="full" pr={1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                    minW="36px"
                    h="36px"
                    borderRadius="full"
                  >
                    <Icon boxSize={5} color="slate.400">
                      {showPasswordConfirmation ? (
                        <EyeSlashIcon />
                      ) : (
                        <EyeIcon />
                      )}
                    </Icon>
                  </Button>
                </InputRightElement>
              </InputGroup>
              {errors.passwordConfirmation?.message && (
                <FormErrorMessage>
                  {errors.passwordConfirmation.message}
                </FormErrorMessage>
              )}
            </FormControl>

            <Button
              type="submit"
              variant="senseiFilled"
              w="full"
              isLoading={isSubmitting}
              mt={3}
            >
              {t("usermgmt.verifyResetPasswordForm.updatePassword")}
            </Button>
          </VStack>
        </form>
      </VStack>
    </VStack>
  );
};
